import React, { useState } from "react";
import { makeStyles, Button, Input, Text, Link, Spinner } from "@fluentui/react-components";
import { CheckmarkCircle16Filled, ErrorCircle16Filled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant Inscription.
 */
export interface InscriptionProps {
  setCurrentView: (view: string) => void; // Fonction pour changer la vue actuelle
  createAccount: (
    data: { username: string; email: string; password: string },
    confirmationCode: string
  ) => Promise<{ message: string }>; // Fonction pour créer un compte utilisateur
  verifyEmail: (email: string, username: string) => Promise<{ message: string }>; // Fonction pour vérifier l'email de l'utilisateur
}

/**
 * Composant Inscription : permet à l'utilisateur de créer un compte en suivant plusieurs étapes.
 * @param {InscriptionProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const Inscription: React.FC<InscriptionProps> = (props: InscriptionProps) => {
  const styles = useStyles();

  // États locaux pour gérer les entrées utilisateur et l'état du composant
  const [username, setUsername] = useState(""); // Nom d'utilisateur
  const [email, setEmail] = useState(""); // Adresse email
  const [verificationCode, setVerificationCode] = useState(""); // Code de vérification
  const [passwords, setPasswords] = useState({ password: "", confirm: "" }); // Mot de passe et confirmation
  const [signupStage, setSignupStage] = useState(1); // Étape actuelle du processus d'inscription
  const [errors, setErrors] = useState(""); // Message d'erreur
  const [loading, setLoading] = useState(false); // Indique si une requête est en cours
  const [success, setSuccess] = useState(false); // Indique si l'inscription a réussi
  const [isEmailVerified, setIsEmailVerified] = useState(false); // Indique si l'email a été vérifié

  /**
   * Retourne une salutation basée sur l'heure actuelle.
   * @returns {string} - "Bonjour" ou "Bonsoir".
   */
  const getGreeting = () => {
    const currentHour = new Date().getHours();
    return currentHour >= 17 ? "Bonsoir" : "Bonjour";
  };

  /**
   * Finalise le processus d'inscription en créant un compte utilisateur.
   */
  const finalizeSignup = async () => {
    setLoading(true);
    setErrors("");
    try {
      await props.createAccount(
        {
          username,
          email,
          password: passwords.password,
        },
        verificationCode
      );
      setSuccess(true);
    } catch (error: any) {
      setErrors(error.message || "Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Envoie un email de vérification à l'utilisateur.
   */
  const handleEmailVerification = async () => {
    setLoading(true);
    setErrors("");
    try {
      await props.verifyEmail(email, username);
      setIsEmailVerified(true);
      setSignupStage(3);
    } catch (error: any) {
      setErrors(error.message || "Échec de l'envoi du code de vérification.");
      setIsEmailVerified(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Valide le nom d'utilisateur.
   * @returns {boolean} - `true` si le nom d'utilisateur est valide, sinon `false`.
   */
  const validateUsername = () => username.trim().length >= 2 && username.trim().length <= 16;

  /**
   * Valide l'adresse email.
   * @returns {boolean} - `true` si l'email est valide, sinon `false`.
   */
  const validateEmail = () => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  /**
   * Valide le mot de passe.
   * @returns {boolean} - `true` si le mot de passe est valide, sinon `false`.
   */
  const validatePassword = () => {
    const strongPassword = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return strongPassword.test(passwords.password);
  };

  /**
   * Vérifie si les deux mots de passe saisis sont identiques.
   * @returns {boolean} - `true` si les mots de passe correspondent, sinon `false`.
   */
  const validateSamePassword = () => {
    const { password, confirm } = passwords;
    return password === confirm;
  };

  /**
   * Gère le clic sur le bouton "Suivant" ou "Créer un compte".
   */
  const handleNextClick = () => {
    setErrors("");
    switch (signupStage) {
      case 1:
        if (validateUsername()) {
          setSignupStage(2);
        } else {
          setErrors("Le nom d'utilisateur doit contenir au moins 5 caractères.");
        }
        break;
      case 2:
        if (validateEmail()) {
          handleEmailVerification();
        } else {
          setErrors("Veuillez entrer une adresse e-mail valide.");
        }
        break;
      case 3:
        if (validatePassword()) {
        } else {
          setErrors("Votre mot de passe est trop faible.");
        }

        if (validateSamePassword()) {
          finalizeSignup();
        } else {
          setErrors("Vos mots de passe ne sont pas identiques.");
        }
        break;
      default:
        break;
    }
  };

  /**
   * Vérifie si le bouton "Suivant" ou "Créer un compte" doit être désactivé.
   * @returns {boolean} - `true` si le bouton doit être désactivé, sinon `false`.
   */
  const isNextDisabled = () => {
    switch (signupStage) {
      case 1:
        return !validateUsername();
      case 2:
        return !validateEmail();
      case 3:
        return !validatePassword() || !verificationCode;
      default:
        return true;
    }
  };

  /**
   * Génère la vue correspondant à l'étape actuelle du processus d'inscription.
   * @returns {JSX.Element} - La vue à afficher.
   */
  const renderView = () => {
    if (loading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner label="Création du compte..." />
        </div>
      );
    }

    if (success) {
      return (
        <div>
          <div className={styles.successText}>
            <CheckmarkCircle16Filled style={{ marginRight: "8px" }} />
            <Text>Inscription réussie!</Text>
          </div>
          <Button appearance="primary" onClick={() => props.setCurrentView("Connexion")}>
            Se connecter
          </Button>
        </div>
      );
    }

    return (
      <>
        {/* Page 1: Nom d'utilisateur */}
        {signupStage === 1 && (
          <div key="step1">
            <h2 className={styles.heading}>Comment devrions-nous vous appeler?</h2>
            <Input
              className={styles.formField}
              placeholder="Nom d'utilisateur"
              value={username}
              onChange={(e: any) => setUsername(e.target.value)}
            />
          </div>
        )}

        {/* Page 2: Adresse courriel */}
        {signupStage === 2 && (
          <div key="step2">
            <h2 className={styles.heading}>Où devrions-nous vous contacter?</h2>
            <Input
              type="email"
              className={styles.formField}
              placeholder="email@example.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
            />
            {!validateEmail() && email.length > 0 && (
              <Text className={styles.errorText}>Veuillez entrer un courriel valide</Text>
            )}
          </div>
        )}

        {/* Page 3: Mot de passe et code de vérification */}
        {signupStage === 3 && (
          <div key="step3">
            <h2 className={styles.heading}>Entrez un mot de passe</h2>
            <Input
              type="password"
              className={styles.formField}
              placeholder="Mot de passe"
              value={passwords.password}
              onChange={(e: any) => setPasswords({ ...passwords, password: e.target.value })}
            />
            <Input
              type="password"
              className={styles.formField}
              placeholder="Confirmez le mot de passe"
              value={passwords.confirm}
              onChange={(e: any) => setPasswords({ ...passwords, confirm: e.target.value })}
            />
            <Input
              className={styles.formField}
              placeholder="Code de vérification"
              value={verificationCode}
              onChange={(e: any) => setVerificationCode(e.target.value)}
            />
            {!validatePassword() && passwords.password.length > 0 && (
              <Text className={styles.errorText}>
                Veuillez entrer un mot de passe contenant plus de 8 caractères, 1 symbole et 1
                nombre
              </Text>
            )}
          </div>
        )}
      </>
    );
  };

  // Rendu du composant
  return (
    <div className={styles.container}>
      {/* Salutation */}
      <h1 className={styles.heading}>
        {getGreeting()}
        {signupStage > 1 && !success ? `, ${username}` : ""}
      </h1>

      {/* Message d'erreur */}
      {errors && (
        <div className={styles.errorText}>
          <ErrorCircle16Filled style={{ marginRight: "8px" }} />
          <Text>{errors}</Text>
        </div>
      )}

      {/* Vue actuelle du processus d'inscription */}
      {renderView()}

      {/* Lien si déjà inscrit */}
      {signupStage < 2 && (
        <Text>
          Déjà inscrit? <Link onClick={() => props.setCurrentView("Connexion")}>Se connecter</Link>
        </Text>
      )}

      {/* Bouton annuler l'inscription */}
      {!loading && signupStage < 3 && (
        <div className={styles.buttonContainer}>
          <Button appearance="secondary" onClick={() => props.setCurrentView("Connexion")}>
            Annuler
          </Button>
        </div>
      )}

      {/* Bouton "Suivant" ou "Créer un compte" */}
      {!loading && !success && (
        <div className={styles.buttonContainer}>
          <Button
            appearance="primary"
            className={!isNextDisabled() ? styles.greenButton : undefined}
            onClick={handleNextClick}
            disabled={isNextDisabled()}
          >
            {signupStage < 3 ? "Suivant" : "Créer un compte"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Inscription;

const useStyles = makeStyles({
  container: {
    display: "flex",
    width: "80%",
    padding: "4vh",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  },
  formField: {
    marginBottom: "12px",
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  heading: {
    margin: "0px 0px 16px 0px",
    width: "100%",
    lineHeight: "30px",
  },
  errorText: {
    color: "red",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  successText: {
    color: "green",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
  greenButton: {
    backgroundColor: "#217346",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
});
